import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  Crown,
  UserCheck,
  UserX,
  TrendingUp,
  Search,
  Download,
  Plus,
  X,
  Loader2,
  Shield,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Trash2,
  Sparkles,
  UserPlus,
  Copy,
  Mail,
  Send,
  RotateCw,
  KeyRound,
  PenLine,
} from 'lucide-react';
import MiniRichTextEditor from '@/components/MiniRichTextEditor';
import ManageAccessDialog from '@/components/ManageAccessDialog';

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'premium', label: 'Premium' },
  { value: 'free', label: 'Gratuitos' },
  { value: 'expired', label: 'Expirados' },
  { value: 'admin', label: 'Administradores' },
];

const MONTHS_PT = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.getDate()} ${MONTHS_PT[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return iso;
  }
}

function StatusBadge({ status, daysRemaining, specificAccessCount }) {
  if (status === 'admin') {
    return (
      <Badge className="bg-gold/20 text-gold border-gold/30">
        <Shield className="w-3 h-3 mr-1" />Admin
      </Badge>
    );
  }
  if (status === 'premium') {
    let cls = 'bg-ruby/20 text-ruby border-ruby/30';
    if (daysRemaining !== null && daysRemaining <= 7) {
      cls = 'bg-red-500/20 text-red-400 border-red-500/30';
    } else if (daysRemaining !== null && daysRemaining <= 30) {
      cls = 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    }
    return (
      <Badge className={cls}>
        <Crown className="w-3 h-3 mr-1" />Premium
      </Badge>
    );
  }
  if (specificAccessCount > 0) {
    return (
      <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/40" title={`${specificAccessCount} acesso(s) específico(s)`}>
        <KeyRound className="w-3 h-3 mr-1" />Acesso Específico
      </Badge>
    );
  }
  if (status === 'expired') {
    return <Badge className="bg-muted/30 text-muted-foreground border-border">Expirado</Badge>;
  }
  return <Badge className="bg-surface-light text-muted-foreground border-border">Gratuito</Badge>;
}

function StatBox({ label, value, icon: Icon, color = 'text-foreground', accent = 'bg-ruby/15' }) {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className={`w-8 h-8 rounded-lg ${accent} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function GrantPremiumDialog({ open, onOpenChange, subscriber, onDone }) {
  const [days, setDays] = useState(365);
  const [label, setLabel] = useState('Premium (atribuído manualmente)');
  const [mode, setMode] = useState('set'); // 'set' = substituir, 'extend' = somar
  const [loading, setLoading] = useState(false);

  const hasActivePremium = !!subscriber?.days_remaining && subscriber.days_remaining > 0;

  // Compute the resulting expiry date so the admin sees exactly what will
  // happen before confirming — eliminates ambiguity between set/extend.
  const previewUntil = (() => {
    const daysInt = parseInt(days, 10) || 0;
    if (!daysInt) return null;
    const now = new Date();
    if (mode === 'extend' && hasActivePremium && subscriber?.premium_until) {
      const current = new Date(subscriber.premium_until);
      const base = current > now ? current : now;
      return new Date(base.getTime() + daysInt * 86400000);
    }
    return new Date(now.getTime() + daysInt * 86400000);
  })();

  const handleGrant = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/subscribers/${subscriber.id}/grant-premium`, {
        duration_days: parseInt(days, 10),
        label,
        mode,
      });
      toast.success(
        mode === 'extend'
          ? `Premium estendido em ${days} dias para ${subscriber.email}.`
          : `Premium definido em ${days} dias para ${subscriber.email}.`
      );
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao conceder Premium.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-ruby">
            <Crown className="w-5 h-5" />Conceder Premium
          </DialogTitle>
          <DialogDescription>
            {subscriber ? <>A atribuir Premium manualmente a <strong className="text-foreground">{subscriber.email}</strong>.</> : ''}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleGrant} className="space-y-4">
          {hasActivePremium && (
            <div className="space-y-2 rounded-lg bg-surface border border-border p-3">
              <Label className="text-sm text-foreground">O utilizador já é Premium</Label>
              <p className="text-xs text-muted-foreground">
                Tem {subscriber.days_remaining} dia(s) restantes. O que queres fazer?
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setMode('set')}
                  data-testid="grant-mode-set"
                  className={`text-xs px-3 py-2 rounded-md border transition-colors text-left ${
                    mode === 'set'
                      ? 'border-ruby bg-ruby/10 text-foreground'
                      : 'border-border text-muted-foreground hover:border-ruby/40'
                  }`}
                >
                  <strong className="block">Substituir</strong>
                  <span className="text-[11px]">Premium passa a ser N dias a contar de hoje</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('extend')}
                  data-testid="grant-mode-extend"
                  className={`text-xs px-3 py-2 rounded-md border transition-colors text-left ${
                    mode === 'extend'
                      ? 'border-ruby bg-ruby/10 text-foreground'
                      : 'border-border text-muted-foreground hover:border-ruby/40'
                  }`}
                >
                  <strong className="block">Estender</strong>
                  <span className="text-[11px]">Soma N dias ao prazo atual</span>
                </button>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="grant-days" className="text-sm text-foreground mb-2 block">Dias exatos</Label>
            <Input
              id="grant-days"
              type="number"
              min="1"
              max="3650"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid="grant-premium-days"
              required
              autoFocus
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[30, 90, 180, 365, 730].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                    parseInt(days, 10) === d
                      ? 'border-ruby bg-ruby/10 text-ruby'
                      : 'border-border text-muted-foreground hover:border-ruby/40'
                  }`}
                >
                  {d} dias
                </button>
              ))}
            </div>
            {previewUntil && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="grant-preview">
                Premium ficará ativo até <strong className="text-foreground">{previewUntil.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="grant-label" className="text-sm text-foreground mb-2 block">Etiqueta interna</Label>
            <Input
              id="grant-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              data-testid="grant-premium-label"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="grant-premium-confirm" className="bg-ruby hover:bg-ruby-light text-white">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</> : <><Crown className="w-4 h-4 mr-2" />Conceder</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RevokePremiumDialog({ open, onOpenChange, subscriber, onDone }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRevoke = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/subscribers/${subscriber.id}/revoke-premium`, { reason });
      toast.success(`Premium revogado para ${subscriber.email}.`);
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao revogar Premium.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />Revogar Premium
          </DialogTitle>
          <DialogDescription>
            {subscriber ? <>A revogar Premium imediatamente para <strong className="text-foreground">{subscriber.email}</strong>. Esta ação é reversível concedendo de novo.</> : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="revoke-reason" className="text-sm text-foreground mb-2 block">Motivo (opcional)</Label>
            <Input
              id="revoke-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ex: reembolso, abuso, pedido do utilizador..."
              data-testid="revoke-premium-reason"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleRevoke} disabled={loading} data-testid="revoke-premium-confirm" className="bg-red-500 hover:bg-red-600 text-white">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A revogar...</> : <><UserX className="w-4 h-4 mr-2" />Revogar</>}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const AUDIENCE_LABELS = {
  all: 'Todos os utilizadores',
  unverified: 'Email não verificado',
  verified: 'Email verificado',
  premium: 'Premium ativos',
  free: 'Apenas gratuitos',
};

function BroadcastDialog({ open, onOpenChange }) {
  const [audience, setAudience] = useState('all');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [audienceCount, setAudienceCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Whenever the dialog opens or audience changes, refresh the recipient count
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get(`/admin/subscribers/broadcast/preview-count?audience=${audience}`)
      .then(({ data }) => { if (!cancelled) setAudienceCount(data.recipient_count); })
      .catch(() => { if (!cancelled) setAudienceCount(null); });
    return () => { cancelled = true; };
  }, [open, audience]);

  const reset = () => {
    setSubject('');
    setBody('');
    setTestEmail('');
    setAudience('all');
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      toast.error('Indica o email de teste.');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      toast.error('Preenche o assunto e o corpo do email.');
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post('/admin/subscribers/broadcast/test', {
        subject: subject.trim(),
        body_html: body,
        test_email: testEmail.trim(),
      });
      // Surface the Brevo message_id so admin can search it on the Brevo logs
      const mid = data?.message_id || '';
      toast.success(`Teste enviado a ${testEmail.trim()}. msg_id: ${mid.slice(0, 40)}…`, { duration: 8000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a enviar o teste.');
    } finally {
      setTesting(false);
    }
  };

  const runDiagnostic = async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get('/admin/email/health');
      setHealth(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a obter diagnóstico.');
    } finally {
      setHealthLoading(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Preenche o assunto e o corpo do email.');
      return;
    }
    const confirmed = window.confirm(
      `Vais enviar o email para ${audienceCount ?? '?'} destinatário(s) (${AUDIENCE_LABELS[audience]}). Continuar?`
    );
    if (!confirmed) return;
    setSending(true);
    try {
      const { data } = await api.post('/admin/subscribers/broadcast', {
        subject: subject.trim(),
        body_html: body,
        audience,
      });
      toast.success(`Email enviado: ${data.sent} sucesso(s), ${data.failed} falha(s).`);
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a enviar.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Mail className="w-5 h-5 text-ruby" />Enviar email aos subscritores
          </DialogTitle>
          <DialogDescription>
            Compõe um email personalizado e envia para o público que escolheres. Recomendado testar primeiro com o teu próprio email.
          </DialogDescription>
        </DialogHeader>

        {/* Brevo diagnostic — explicitly callable when emails aren't arriving */}
        <div className="rounded-md border border-border bg-white/5 p-3" data-testid="brevo-diagnostic-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Os emails de teste não chegam? Corre o diagnóstico para verificar a Brevo.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={runDiagnostic}
              disabled={healthLoading}
              data-testid="btn-brevo-diagnostic"
            >
              {healthLoading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
              Diagnóstico Brevo
            </Button>
          </div>
          {health && (
            <div className="mt-3 text-xs space-y-2" data-testid="brevo-diagnostic-result">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <span className="text-muted-foreground">API key presente:</span>
                <span className={health.health.api_key_present ? 'text-emerald-400' : 'text-ruby'}>
                  {health.health.api_key_present ? `Sim (${health.health.api_key_preview})` : 'NÃO — falta BREVO_API_KEY no ambiente'}
                </span>
                <span className="text-muted-foreground">Brevo aceita a key:</span>
                <span className={health.health.brevo_reachable ? 'text-emerald-400' : 'text-ruby'}>
                  {health.health.brevo_reachable ? 'Sim' : `Não — ${health.health.error || 'erro desconhecido'}`}
                </span>
                {health.health.account_email && (
                  <>
                    <span className="text-muted-foreground">Conta Brevo:</span>
                    <span className="text-foreground">{health.health.account_email}</span>
                  </>
                )}
                <span className="text-muted-foreground">Remetente (FROM):</span>
                <span className="text-foreground">{health.health.sender_email}</span>
                <span className="text-muted-foreground">Créditos restantes:</span>
                <span className={health.health.credits && health.health.credits > 50 ? 'text-emerald-400' : 'text-amber-400'}>
                  {health.health.credits ?? '—'}
                </span>
                <span className="text-muted-foreground">FRONTEND_URL (nos links):</span>
                <span className="text-foreground break-all">{health.health.frontend_url}</span>
              </div>
              {health.recent_broadcasts?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-muted-foreground">
                    Últimos {health.recent_broadcasts.length} broadcasts (clica para expandir)
                  </summary>
                  <div className="mt-2 space-y-1">
                    {health.recent_broadcasts.map((b, i) => (
                      <div key={i} className="p-2 rounded bg-black/30">
                        <p className="text-foreground truncate">{b.subject}</p>
                        <p className="text-muted-foreground">
                          {b.audience} · ✅ {b.sent_count} · ❌ {b.failed_count}
                          {b.failed_count > 0 && b.first_failures.length > 0 && (
                            <span className="text-ruby"> · ex: {b.first_failures[0].email} → {b.first_failures[0].error?.slice(0, 80)}</span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Destinatários</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="broadcast-audience-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(AUDIENCE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {audienceCount === null
                ? 'A calcular destinatários…'
                : `${audienceCount} destinatário(s) selecionado(s).`}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="broadcast-subject" className="text-sm">Assunto</Label>
            <Input
              id="broadcast-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex: Novidades desta semana no Intimate.pt"
              data-testid="broadcast-subject-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">
              Corpo do email <span className="text-muted-foreground/70">— suporta negrito, itálico e links</span>
            </Label>
            <MiniRichTextEditor
              value={body}
              onChange={setBody}
              placeholder="Escreve a mensagem para os subscritores…"
              minHeight="180px"
              data-testid="broadcast-body-input"
            />
            <p className="text-[11px] text-muted-foreground">O email vai usar a saudação automática &quot;Olá [nome],&quot; e o estilo branded do Intimate.pt.</p>
          </div>

          <div className="border-t border-border pt-4 space-y-2">
            <Label htmlFor="broadcast-test-email" className="text-sm flex items-center gap-2">
              <Send className="w-3.5 h-3.5 text-muted-foreground" />Enviar uma cópia de teste primeiro
            </Label>
            <div className="flex gap-2">
              <Input
                id="broadcast-test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="o-teu-email@exemplo.com"
                data-testid="broadcast-test-email-input"
                className="flex-1"
              />
              <Button
                type="button"
                onClick={handleSendTest}
                disabled={testing || !testEmail.trim() || !subject.trim() || !body.trim()}
                variant="outline"
                data-testid="broadcast-test-send"
              >
                {testing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar...</> : <><Send className="w-4 h-4 mr-2" />Enviar teste</>}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 mt-4 pt-4 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !body.trim() || !audienceCount}
            data-testid="broadcast-send"
            className="bg-ruby hover:bg-ruby-light text-white"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar para {audienceCount}...</>
            ) : (
              <><Mail className="w-4 h-4 mr-2" />Enviar para {audienceCount ?? '?'} pessoa(s)</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateSubscriberDialog({ open, onOpenChange, onDone }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [premiumDays, setPremiumDays] = useState('365');
  const [grantPremium, setGrantPremium] = useState(true);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);

  const reset = () => {
    setEmail('');
    setName('');
    setPassword('');
    setPremiumDays('365');
    setGrantPremium(true);
    setGenerated(null);
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Email é obrigatório.');
      return;
    }
    setLoading(true);
    try {
      const body = {
        email: email.trim(),
        name: name.trim() || null,
        password: password.trim() || null,
        premium_days: grantPremium ? Number(premiumDays) || null : null,
        mark_verified: true,
      };
      const { data } = await api.post('/admin/subscribers', body);
      toast.success(`Subscritor ${data.email} criado.`);
      setGenerated({
        email: data.email,
        password: data.generated_password,
        is_premium: data.is_premium,
        premium_until: data.premium_until,
      });
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar subscritor.');
    } finally {
      setLoading(false);
    }
  };

  const copyCredentials = async () => {
    if (!generated) return;
    const text = `Email: ${generated.email}\nPassword: ${generated.password || '(definida)'}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Credenciais copiadas.');
    } catch {
      toast.error('Não foi possível copiar — copia manualmente.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <UserPlus className="w-5 h-5 text-ruby" />Novo subscritor
          </DialogTitle>
          <DialogDescription>
            Cria uma conta manualmente — útil para clientes que compraram fora da plataforma.
          </DialogDescription>
        </DialogHeader>

        {generated ? (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-2">
              <p className="text-sm text-emerald-300">Conta criada com sucesso. Partilha estas credenciais com o subscritor:</p>
              <div className="font-mono text-xs bg-background/50 p-3 rounded border border-border space-y-1">
                <div><span className="text-muted-foreground">Email:</span> <span className="text-foreground">{generated.email}</span></div>
                {generated.password && (
                  <div><span className="text-muted-foreground">Password:</span> <span className="text-foreground">{generated.password}</span></div>
                )}
                {generated.is_premium && (
                  <div><span className="text-muted-foreground">Premium até:</span> <span className="text-gold">{generated.premium_until ? new Date(generated.premium_until).toLocaleDateString('pt-PT') : '—'}</span></div>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">A password só é mostrada agora. Pede ao utilizador para a alterar em /conta.</p>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              {generated.password && (
                <Button type="button" variant="outline" onClick={copyCredentials} data-testid="copy-credentials">
                  <Copy className="w-4 h-4 mr-2" />Copiar credenciais
                </Button>
              )}
              <Button type="button" onClick={() => handleClose(false)} className="bg-ruby hover:bg-ruby-light">
                Concluído
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-sub-email">Email *</Label>
              <Input
                id="new-sub-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="leitor@exemplo.com"
                data-testid="new-sub-email"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-sub-name">Nome (opcional)</Label>
              <Input
                id="new-sub-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome do subscritor"
                data-testid="new-sub-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-sub-password">Password (opcional)</Label>
              <Input
                id="new-sub-password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Deixa em branco para gerar automaticamente"
                data-testid="new-sub-password"
              />
              <p className="text-[11px] text-muted-foreground">Se deixares em branco, geramos uma password aleatória e mostramos-ta uma só vez.</p>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface border border-border">
              <div>
                <Label htmlFor="new-sub-premium" className="text-foreground">Ativar Premium</Label>
                <p className="text-xs text-muted-foreground">Pode ser alterado depois</p>
              </div>
              <input
                id="new-sub-premium"
                type="checkbox"
                checked={grantPremium}
                onChange={(e) => setGrantPremium(e.target.checked)}
                className="w-5 h-5 accent-ruby"
                data-testid="new-sub-grant-premium"
              />
            </div>
            {grantPremium && (
              <div className="space-y-2">
                <Label htmlFor="new-sub-days">Dias de Premium</Label>
                <Input
                  id="new-sub-days"
                  type="number"
                  min="1"
                  max="3650"
                  value={premiumDays}
                  onChange={(e) => setPremiumDays(e.target.value)}
                  data-testid="new-sub-premium-days"
                />
                <p className="text-[11px] text-muted-foreground">Sugestões comuns: 30 (mensal), 365 (anual), 3650 (vitalício).</p>
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="new-sub-submit" className="bg-ruby hover:bg-ruby-light">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A criar...</> : <><UserPlus className="w-4 h-4 mr-2" />Criar subscritor</>}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeleteSubscriberDialog({ open, onOpenChange, subscriber, onDone }) {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/admin/subscribers/${subscriber.id}`);
      toast.success(`Conta ${subscriber.email} eliminada.`);
      onOpenChange(false);
      setConfirmText('');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao eliminar conta.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setConfirmText(''); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <Trash2 className="w-5 h-5" />Eliminar conta
          </DialogTitle>
          <DialogDescription>
            {subscriber ? (
              <>Vais eliminar definitivamente <strong className="text-foreground">{subscriber.email}</strong> e todos os dados associados (favoritos, histórico, confissões submetidas, mensagens). Esta ação é <strong>irreversível</strong>.</>
            ) : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="delete-confirm" className="text-sm text-foreground mb-2 block">
              Escreve <code className="text-red-400">ELIMINAR</code> para confirmar
            </Label>
            <Input
              id="delete-confirm"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ELIMINAR"
              data-testid="delete-subscriber-confirm-input"
              autoComplete="off"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              disabled={loading || confirmText.trim().toUpperCase() !== 'ELIMINAR'}
              data-testid="delete-subscriber-confirm"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A eliminar...</> : <><Trash2 className="w-4 h-4 mr-2" />Eliminar</>}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSubscribers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [grantTarget, setGrantTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [accessTarget, setAccessTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [penNameTarget, setPenNameTarget] = useState(null);
  const [penNameValue, setPenNameValue] = useState('');
  const [penNameSaving, setPenNameSaving] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const LIMIT = 50;

  // Admin guard
  useEffect(() => {
    if (user && !user.is_admin) navigate('/');
  }, [user, navigate]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when filter changes
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setPage(0); }, [filter, debouncedSearch]);

  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/admin/subscribers/stats');
        if (!cancelled) setStats(data);
      } catch (err) {
        console.error('Stats error', err);
      }
    })();
    return () => { cancelled = true; };
  }, [tick]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filter !== 'all') params.append('status', filter);
        if (debouncedSearch) params.append('search', debouncedSearch);
        params.append('limit', String(LIMIT));
        params.append('skip', String(page * LIMIT));
        const { data } = await api.get(`/admin/subscribers?${params.toString()}`);
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (err) {
        if (!cancelled) toast.error('Erro ao carregar subscritores.');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter, debouncedSearch, page, tick]);

  // Bumping `tick` forces both effects above to re-run. Used after admin
  // mutations (grant, revoke, delete, create…) to pull fresh data.
  const refresh = () => setTick((t) => t + 1);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      const response = await api.get(`/admin/subscribers/export.csv?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `subscritores_intimate_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV exportado.');
    } catch (err) {
      toast.error('Erro ao exportar CSV.');
      console.error(err);
    }
  };

  const handleCleanupTestAccounts = async () => {
    setCleanupLoading(true);
    try {
      const { data } = await api.post('/admin/subscribers/cleanup-test-accounts');
      toast.success(`${data.deleted_count} conta(s) de teste eliminada(s).`);
      setCleanupOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao limpar contas de teste.');
    } finally {
      setCleanupLoading(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            data-testid="subscribers-back"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />Voltar ao painel
          </button>

          <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-ruby to-ruby-light flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Subscritores</h1>
                <p className="text-muted-foreground">Gerir utilizadores e ativações Premium</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setBroadcastOpen(true)}
                data-testid="subscribers-broadcast"
                className="rounded-full bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                <Mail className="w-4 h-4 mr-2" />Enviar email
              </Button>
              <Button
                onClick={() => setCreateOpen(true)}
                data-testid="subscribers-create"
                className="rounded-full bg-ruby hover:bg-ruby-light text-white"
              >
                <UserPlus className="w-4 h-4 mr-2" />Novo subscritor
              </Button>
              <Button
                onClick={() => setCleanupOpen(true)}
                data-testid="subscribers-cleanup-test"
                variant="outline"
                className="rounded-full border-amber-500/50 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
              >
                <Sparkles className="w-4 h-4 mr-2" />Limpar contas de teste
              </Button>
              <Button
                onClick={handleExport}
                data-testid="subscribers-export-csv"
                variant="outline"
                className="rounded-full"
              >
                <Download className="w-4 h-4 mr-2" />Exportar CSV
              </Button>
            </div>
          </header>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
              <StatBox label="Total" value={stats.total} icon={Users} color="text-foreground" accent="bg-blue-500/15" />
              <StatBox label="Premium ativos" value={stats.premium_active} icon={Crown} color="text-ruby" accent="bg-ruby/15" />
              <StatBox label="Gratuitos" value={stats.free} icon={UserCheck} color="text-muted-foreground" accent="bg-muted/30" />
              <StatBox label="Expirados" value={stats.premium_expired} icon={UserX} color="text-amber-400" accent="bg-amber-500/15" />
              <StatBox label="Novos (7d)" value={stats.new_this_week} icon={TrendingUp} color="text-green-400" accent="bg-green-500/15" />
              <StatBox label="Premium pendente" value={stats.pending_premium} icon={Plus} color="text-blue-400" accent="bg-blue-500/15" />
            </div>
          )}

          {/* Filters */}
          <Card className="bg-surface border-border mb-4">
            <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-center">
              <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setFilter(f.value)}
                    data-testid={`filter-${f.value}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      filter === f.value
                        ? 'border-ruby bg-ruby/10 text-ruby'
                        : 'border-border text-muted-foreground hover:border-ruby/40'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="md:ml-auto relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar por email ou nome..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="subscribers-search"
                  className="pl-9 pr-9 w-full md:w-72"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="bg-surface border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="subscribers-table">
                <thead className="bg-surface-light border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Utilizador</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Estado</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Premium até</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Dias</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Registo</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="py-12 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-ruby mx-auto" />
                    </td></tr>
                  ) : items.length === 0 ? (
                    <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">
                      Nenhum subscritor encontrado.
                    </td></tr>
                  ) : (
                    items.map((s) => (
                      <tr key={s.id} className="border-b border-border last:border-0 hover:bg-surface-light/50">
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-medium text-foreground">{s.name || '—'}</span>
                            {s.pen_name && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] text-purple-300 mt-0.5"
                                title="Nickname público — substitui o nome real nos contos"
                                data-testid={`pen-name-hint-${s.id}`}
                              >
                                <PenLine className="w-2.5 h-2.5" />
                                publica como <strong>{s.pen_name}</strong>
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">{s.email}</span>
                            {!s.email_verified && (
                              <button
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/subscribers/${s.id}/resend-verification`);
                                    toast.success(`Email de verificação reenviado para ${s.email}.`);
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Erro a reenviar.');
                                  }
                                }}
                                data-testid={`resend-verify-${s.id}`}
                                className="inline-flex items-center gap-1 text-[10px] text-amber-400 hover:text-amber-300 mt-0.5 transition-colors"
                                title="Reenviar email de verificação"
                              >
                                <RotateCw className="w-2.5 h-2.5" />
                                Email não verificado — reenviar
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={s.status} daysRemaining={s.days_remaining} specificAccessCount={s.specific_access_count} /></td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(s.premium_until)}</td>
                        <td className="px-4 py-3">
                          {s.days_remaining !== null ? (
                            <span className={`font-medium ${
                              s.days_remaining <= 7 ? 'text-red-400' :
                              s.days_remaining <= 30 ? 'text-amber-400' : 'text-foreground'
                            }`}>{s.days_remaining}</span>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(s.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {!s.is_admin && (
                              <button
                                onClick={() => setGrantTarget(s)}
                                data-testid={`grant-btn-${s.id}`}
                                className="text-xs px-2.5 py-1 rounded-full bg-ruby/10 text-ruby hover:bg-ruby/20 transition-colors"
                              >
                                + Premium
                              </button>
                            )}
                            {!s.is_admin && s.status === 'premium' && (
                              <button
                                onClick={() => setRevokeTarget(s)}
                                data-testid={`revoke-btn-${s.id}`}
                                className="text-xs px-2.5 py-1 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                              >
                                Revogar
                              </button>
                            )}
                            {!s.is_admin && (
                              <button
                                onClick={() => setAccessTarget(s)}
                                data-testid={`access-btn-${s.id}`}
                                title="Gerir Acesso Específico"
                                className="text-xs px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 transition-colors flex items-center gap-1"
                              >
                                <KeyRound className="w-3 h-3" />
                                Acesso
                              </button>
                            )}
                            {!s.is_admin && (
                              <button
                                onClick={async () => {
                                  try {
                                    const next = !s.is_official;
                                    await api.patch(`/admin/subscribers/${s.id}/official`, { is_official: next });
                                    toast.success(next ? 'Marcado como Autor Oficial Intimate' : 'Removido de Autor Oficial');
                                    refresh();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Erro ao atualizar');
                                  }
                                }}
                                data-testid={`official-toggle-${s.id}`}
                                title={s.is_official ? 'Remover como Autor Oficial' : 'Marcar como Autor Oficial Intimate'}
                                className={
                                  'text-xs px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 ' +
                                  (s.is_official
                                    ? 'bg-gold/20 text-gold hover:bg-gold/30'
                                    : 'bg-white/5 text-muted-foreground hover:bg-white/10')
                                }
                              >
                                <Sparkles className="w-3 h-3" />
                                {s.is_official ? 'Oficial' : 'Comunidade'}
                              </button>
                            )}
                            {!s.is_admin && (
                              <button
                                onClick={() => {
                                  setPenNameTarget(s);
                                  setPenNameValue(s.pen_name || '');
                                }}
                                data-testid={`pen-name-btn-${s.id}`}
                                title={s.pen_name ? `Alterar nome de autor (atual: ${s.pen_name})` : 'Definir nome de autor'}
                                className={
                                  'text-xs px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 ' +
                                  (s.pen_name
                                    ? 'bg-purple-500/15 text-purple-300 hover:bg-purple-500/25'
                                    : 'bg-white/5 text-muted-foreground hover:bg-white/10')
                                }
                              >
                                <PenLine className="w-3 h-3" />
                                {s.pen_name ? 'Nome' : 'Nome'}
                              </button>
                            )}
                            {!s.is_admin && (
                              <button
                                onClick={() => setDeleteTarget(s)}
                                data-testid={`delete-btn-${s.id}`}
                                title="Eliminar conta"
                                className="text-xs p-1.5 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > LIMIT && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  A mostrar {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} de {total}
                </p>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    data-testid="subscribers-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">Página {page + 1} de {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    data-testid="subscribers-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </main>

      <GrantPremiumDialog
        open={!!grantTarget}
        onOpenChange={(o) => !o && setGrantTarget(null)}
        subscriber={grantTarget}
        onDone={refresh}
      />
      <RevokePremiumDialog
        open={!!revokeTarget}
        onOpenChange={(o) => !o && setRevokeTarget(null)}
        subscriber={revokeTarget}
        onDone={refresh}
      />
      <ManageAccessDialog
        open={!!accessTarget}
        onOpenChange={(o) => !o && setAccessTarget(null)}
        subscriber={accessTarget}
        onDone={refresh}
      />
      <DeleteSubscriberDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        subscriber={deleteTarget}
        onDone={refresh}
      />
      <Dialog open={!!penNameTarget} onOpenChange={(o) => { if (!o) { setPenNameTarget(null); setPenNameValue(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-ruby" />
              Nome de autor público
            </DialogTitle>
            <DialogDescription>
              {penNameTarget && (
                <>
                  Define um nickname que aparecerá publicamente nos contos e artigos de{' '}
                  <strong className="text-foreground">{penNameTarget.name || penNameTarget.email}</strong>{' '}
                  em vez do nome real. Deixa em branco para voltar ao nome da conta.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="pen-name-input">Nickname (máx. 60 caracteres)</Label>
            <Input
              id="pen-name-input"
              value={penNameValue}
              onChange={(e) => setPenNameValue(e.target.value.slice(0, 60))}
              placeholder="Ex.: Luísa S., Lua, Vermelho..."
              className="bg-background border-border"
              data-testid="pen-name-input"
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-right">{penNameValue.length}/60</p>
            {penNameTarget?.pen_name && (
              <p className="text-xs text-muted-foreground">
                Nickname atual: <strong className="text-foreground">{penNameTarget.pen_name}</strong>
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setPenNameTarget(null); setPenNameValue(''); }}
              className="border-border"
            >
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (!penNameTarget) return;
                setPenNameSaving(true);
                try {
                  const res = await api.patch(
                    `/admin/subscribers/${penNameTarget.id}/pen-name`,
                    { pen_name: penNameValue.trim() }
                  );
                  const { stories_updated, articles_updated } = res.data || {};
                  toast.success(
                    penNameValue.trim()
                      ? `Nickname aplicado (${stories_updated || 0} contos, ${articles_updated || 0} artigos atualizados)`
                      : 'Nickname removido — voltou ao nome real'
                  );
                  setPenNameTarget(null);
                  setPenNameValue('');
                  refresh();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Erro ao guardar');
                } finally {
                  setPenNameSaving(false);
                }
              }}
              disabled={penNameSaving}
              data-testid="pen-name-save"
              className="bg-ruby hover:bg-ruby-light"
            >
              {penNameSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenLine className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateSubscriberDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onDone={refresh}
      />

      <BroadcastDialog open={broadcastOpen} onOpenChange={setBroadcastOpen} />

      {/* Cleanup test accounts confirmation */}
      <Dialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-300">
              <Sparkles className="w-5 h-5" />Limpar contas de teste
            </DialogTitle>
            <DialogDescription>
              Vai eliminar todas as contas com email <code>@example.com</code>, <code>@example.org</code>, <code>@test.com</code>, <code>@localhost</code>, ou cujo local-part comece por <code>test_user_</code>. Contas reais e admins não são tocadas. Esta ação é irreversível.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => setCleanupOpen(false)} disabled={cleanupLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleCleanupTestAccounts}
              disabled={cleanupLoading}
              data-testid="cleanup-test-confirm"
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {cleanupLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A limpar...</> : <><Sparkles className="w-4 h-4 mr-2" />Limpar agora</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
